/**
 * The six preset team roles + their hierarchy priorities.
 *
 * Priority is used by the hierarchy-override rule (ADR 0007): a LEAD cannot override a
 * Respondent who is OWNER, etc. Custom roles inherit priority from the preset they were
 * derived from; if no preset, they default to MEMBER (40).
 *
 * Source of truth — DO NOT duplicate this list in services. Always import from here.
 */
export const PRESET_ROLES = ['OWNER', 'MANAGER', 'LEAD', 'MEMBER', 'OUTSTAFF', 'VIEWER'] as const;
export type PresetRole = (typeof PRESET_ROLES)[number];

/**
 * Roles that can be ASSIGNED to someone (on invite or role-change). OWNER is
 * excluded — ownership moves only via transferOwnership. Single source of truth
 * so invite UIs + DTOs don't each re-derive the exclusion.
 */
export type AssignablePresetRole = Exclude<PresetRole, 'OWNER'>;
export const ASSIGNABLE_PRESET_ROLES: readonly AssignablePresetRole[] = PRESET_ROLES.filter(
  (r): r is AssignablePresetRole => r !== 'OWNER',
);

export const PRESET_ROLE_PRIORITY: Readonly<Record<PresetRole, number>> = {
  OWNER: 100,
  MANAGER: 80,
  LEAD: 60,
  MEMBER: 40,
  OUTSTAFF: 30,
  VIEWER: 20,
};

export function rolePriority(role: PresetRole): number {
  return PRESET_ROLE_PRIORITY[role];
}

export function isAtLeast(actor: PresetRole, threshold: PresetRole): boolean {
  return rolePriority(actor) >= rolePriority(threshold);
}

export function outranks(higher: PresetRole, lower: PresetRole): boolean {
  return rolePriority(higher) > rolePriority(lower);
}
